/* ============================================================================
   src/ocVeredito.js — a leitura da comparacao RiskMind x agencia, para TODA ficha

   Antes deste arquivo, o bloco de veredito so aparecia quando a tabela
   "oc_rating_eval" tinha linha para a companhia — o que acontece em uma minoria.
   Braskem tinha; Casas Bahia e Tegma nao. O resultado era uma ficha que ora
   explicava a comparacao, ora ficava muda, sem que o usuario soubesse por que.

   Silencio nao e um estado. Ausencia de agencia e um estado; nota so em escala
   nacional e outro; divergencia aberta e outro. Cada um tem leitura propria e
   cada um precisa estar escrito na tela.

   ----------------------------------------------------------------------------
   A REGRA QUE NAO SE QUEBRA: LETRA NAO SE CONVERTE ENTRE ESCALAS

   "AAA(bra)", "brAAA" e "AAA.br" sao ordenacoes RELATIVAS dentro do Brasil,
   ancoradas no soberano. A escala global nao tem essa ancora. Medido nos nossos
   proprios pares (61 pares, 24 companhias, mesma agencia, ate 90 dias):

       AAA(bra)  ->  BB+ (6)   A (3)   BBB (2)   BB (1)
       brAA-     ->  AA (1)    B+ (1)

   Seis degraus de dispersao na primeira linha, doze na segunda. A dispersao nao
   e ruido: quem exporta em moeda forte ou tem matriz estrangeira fura o teto
   soberano e aparece em A ou BBB; quem nao fura fica preso perto de BB+, que e
   onde o Brasil esta. **A mesma letra nacional significa coisas diferentes.**

   Por isso, quando a nota e nacional, este modulo NAO estima PD e NAO calcula Δ.
   O que ele faz e comparar POSICAO RELATIVA DENTRO DE CADA ESCALA — o quao
   fundo cada lado poe a companhia na sua propria regua. Isso e legitimo e e
   dito com todas as letras na tela.

   ----------------------------------------------------------------------------
   OS SEIS ESTADOS

     sem_contraparte    nenhuma agencia protocolou. Ausencia de DADO, nao de acao.
     avaliado           "oc_rating_eval" ja julgou um evento; o banco manda.
     divergencia        RiskMind materialmente mais severo que a agencia.
     riskmind_mais_brando   o inverso — e isto tambem tem de aparecer.
     convergencia       os dois na mesma faixa.
     sem_par_corrente   ha cobertura, mas nao ha par vigente para comparar hoje.

   "riskmind_mais_brando" existe de proposito. Um painel que so sabe dizer
   "estamos mais duros que a agencia" nao e um painel, e um argumento de venda.
   ========================================================================== */

export const ORDEM_RM = ['AAA', 'AA', 'A', 'BBB', 'BB', 'B', 'CCC', 'CC', 'C', 'D'];

export const ORDEM_AGENCIA = [
  'AAA', 'AA+', 'AA', 'AA-', 'A+', 'A', 'A-',
  'BBB+', 'BBB', 'BBB-', 'BB+', 'BB', 'BB-', 'B+', 'B', 'B-',
  'CCC+', 'CCC', 'CCC-', 'CC', 'C', 'D',
];

/* Limiares. Em p.p. para PD (escala global) e em fracao da regua para posicao
   (escala nacional). 10 p.p. e uma diferenca que muda decisao de credito; 0,20
   de regua sao cerca de dois degraus na escala do motor. */
export const LIMIAR_PP = 10;
export const LIMIAR_POSICAO = 0.20;

/**
 * Tira o MARCADOR de escala nacional sem converter nada: brAAA -> AAA,
 * AAA(bra) -> AAA, AAA.br -> AAA. Serve so para achar a posicao da nota DENTRO
 * da propria escala nacional, que tem as mesmas letras da global mas outro
 * significado. Nao e conversao e nao deve ser usada como se fosse.
 */
export function semMarcador(rating) {
  let s = String(rating == null ? '' : rating).trim();
  s = s.replace(/\s*\((bra|br)\)\s*$/i, '');
  s = s.replace(/\.br\s*$/i, '');
  s = s.replace(/^br(?=[A-Za-z])/, '');
  return s.toUpperCase();
}

/** Posicao 0..1 dentro da propria regua. 0 = topo, 1 = fundo. null se nao achar. */
export function posicao(rating, ordem) {
  const i = ordem.indexOf(semMarcador(rating));
  if (i < 0) return null;
  return i / (ordem.length - 1);
}

/**
 * A nota VIGENTE de cada agencia — a mais recente que cada uma publicou.
 *
 * Existe porque o card "Ratings de agencia comparaveis" so aparecia em parte
 * das fichas: ele vinha de "oc_painel.payload", gerado por fora, e ficava vazio
 * para quem o gerador nao cobriu. A trilha ja recebe o track inteiro — dela da
 * para derivar isso sempre, e derivado do dado e melhor do que copiado.
 *
 * A escala viaja junto de proposito. Nota sem escala ao lado convida a comparar
 * letra com letra, que e exatamente o que nao se pode fazer.
 */
export function notasVigentes(track, fonteRm) {
  const rm = fonteRm || 'riskmind';
  const pts = (Array.isArray(track) ? track : []).filter((p) => p && p.fonte !== rm && p.rating);
  const porFonte = new Map();
  for (const p of pts) {
    const d = String(p.obs_date || '').slice(0, 10);
    const atual = porFonte.get(p.fonte);
    if (!atual || d > atual.desde) {
      porFonte.set(p.fonte, { fonte: p.fonte, rating: p.rating, escala: p.escala || null, desde: d });
    }
  }
  return [...porFonte.values()].sort((a, b) => String(a.fonte).localeCompare(String(b.fonte)));
}

function maisRecente(vereditos) {
  const v = (Array.isArray(vereditos) ? vereditos : []).filter(Boolean);
  if (!v.length) return null;
  return v.slice().sort((a, b) => String(a.data_pub || '').localeCompare(String(b.data_pub || '')))[v.length - 1];
}

/**
 * A leitura da ficha. SEMPRE devolve um objeto — nunca null, nunca silencio.
 *
 * @param {Object}  p
 * @param {Array}   p.linhas     saida de montarLinhas, mais recente primeiro
 * @param {Object}  p.cobertura  saida de montarLinhas
 * @param {Array}   p.vereditos  linhas de oc_rating_eval (podem nao existir)
 * @returns {{chave:string, rotulo:string, tom:string, texto:string, detalhe:(Object|null)}}
 */
export function lerVeredito({ linhas, cobertura, vereditos }) {
  const L = Array.isArray(linhas) ? linhas : [];
  const cob = cobertura || {};

  if (!cob.temAgencia) {
    return {
      chave: 'sem_contraparte',
      rotulo: 'sem contraparte de agencia',
      tom: 'neutro',
      detalhe: null,
      texto: 'Nenhuma agencia protocolou relatorio de rating no IPE da CVM para esta '
        + 'companhia. A ausencia e de DADO, nao de acao: protocolar e voluntario. '
        + 'Sem contraparte nao ha comparacao — a leitura do RiskMind vale sozinha.',
    };
  }

  /* O banco ja julgou um evento. O julgamento dele manda: foi feito sobre a
     acao datada, com a leitura as-of daquele momento, e nao sobre o estado de
     hoje. Recalcular por cima seria trocar registro por opiniao. */
  const v = maisRecente(vereditos);
  if (v && v.veredito) {
    const pd = v.rm_pd_asof == null ? null : Number(v.rm_pd_asof);
    return {
      chave: 'avaliado',
      rotulo: String(v.veredito),
      tom: /antecip|coerenc/i.test(v.veredito) ? 'bom' : /diverg/i.test(v.veredito) ? 'atencao' : 'neutro',
      detalhe: v,
      texto: String(v.agencia || 'agencia') + ' marcou ' + String(v.rating_novo || '?')
        + ' (' + String(v.escala || '?') + ') em ' + String(v.data_pub || '?').slice(0, 10)
        + '; o RiskMind ja marcava ' + String(v.rm_rating_asof || '?')
        + (pd == null ? '' : ' com PD ' + pd.toFixed(1) + '%')
        + (v.antecedencia_meses > 0 ? ', ha ' + v.antecedencia_meses + ' meses' : '') + '.',
    };
  }

  const atual = L.find((l) => l.rmRating && l.agRating) || null;
  if (!atual) {
    return {
      chave: 'sem_par_corrente',
      rotulo: 'sem par vigente',
      tom: 'neutro',
      detalhe: null,
      texto: 'Ha cobertura de agencia no historico, mas nao ha um trimestre em que as duas '
        + 'leituras existam ao mesmo tempo. Sem par nao ha comparacao honesta a fazer.',
    };
  }

  const nacional = !cob.temGlobal;

  /* Escala global: a comparacao que vale e a de PD, e ela ja esta calculada
     na linha. Nao reimplementar aqui — uma segunda formula e uma segunda
     verdade. */
  if (!nacional && atual.delta != null) {
    const d = atual.delta;
    const comum = 'RiskMind ' + atual.rmRating + ' com PD ' + atual.rmPd.toFixed(2).replace('.', ',')
      + '% contra ' + atual.agFonte + ' ' + atual.agRating + ' em escala global, cuja PD implicita '
      + 'e ' + atual.agPd.toFixed(2).replace('.', ',') + '%. Diferenca de '
      + (d > 0 ? '+' : '') + d.toFixed(2).replace('.', ',') + ' p.p.';
    if (d >= LIMIAR_PP) {
      return {
        chave: 'divergencia', rotulo: 'divergencia aberta', tom: 'atencao', detalhe: atual,
        texto: comum + ' O motor poe a companhia materialmente mais perto do default do que a '
          + 'agencia. Divergencia aberta nao e erro de nenhum dos dois: sao objetos diferentes '
          + '(media de coorte de longo prazo contra saida point-in-time). O que ela indica e '
          + 'onde olhar.',
      };
    }
    if (d <= -LIMIAR_PP) {
      return {
        chave: 'riskmind_mais_brando', rotulo: 'RiskMind mais brando', tom: 'atencao', detalhe: atual,
        texto: comum + ' Aqui o motor e MAIS brando que a agencia. Vale registrar com o mesmo '
          + 'destaque do caso inverso — painel que so acusa quando esta mais duro nao e painel.',
      };
    }
    return {
      chave: 'convergencia', rotulo: 'convergencia', tom: 'bom', detalhe: atual,
      texto: comum + ' As duas leituras estao na mesma faixa.',
    };
  }

  /* Escala nacional: nao ha PD publicada, entao nao ha Δ. O que da para comparar
     e a POSICAO RELATIVA de cada nota dentro da propria regua. */
  const pRm = posicao(atual.rmRating, ORDEM_RM);
  const pAg = posicao(atual.agRating, ORDEM_AGENCIA);
  const preambulo = 'A nota da agencia esta em escala nacional (' + atual.agRating + ', '
    + atual.agFonte + '), que e uma ordenacao relativa dentro do Brasil, ancorada no soberano, '
    + 'e NAO tem taxa de default publicada. Por isso nao ha PD implicita nem Δ nesta ficha — '
    + 'converter letra entre escalas seria inventar dado. ';

  if (pRm == null || pAg == null) {
    return {
      chave: 'sem_par_corrente', rotulo: 'escala nacional', tom: 'neutro', detalhe: atual,
      texto: preambulo + 'A nota tambem nao foi reconhecida na regua padrao, entao nem a '
        + 'comparacao por posicao relativa e possivel.',
    };
  }

  const gap = pRm - pAg;
  const onde = (p) => (p >= 0.72 ? 'no fundo' : p >= 0.45 ? 'no meio-baixo' : p >= 0.25 ? 'no meio' : 'no topo');
  const comum = preambulo + 'O que da para comparar e a posicao dentro de cada regua: o RiskMind '
    + 'poe a companhia ' + onde(pRm) + ' da escala dele (' + atual.rmRating + '), e a agencia a poe '
    + onde(pAg) + ' da escala nacional (' + atual.agRating + ').';

  if (gap >= LIMIAR_POSICAO) {
    return {
      chave: 'divergencia', rotulo: 'divergencia aberta', tom: 'atencao', detalhe: atual,
      texto: comum + ' O motor esta materialmente mais severo. Sem PD publicada para a escala '
        + 'nacional, esta e a leitura mais forte que os dados sustentam.',
    };
  }
  if (gap <= -LIMIAR_POSICAO) {
    return {
      chave: 'riskmind_mais_brando', rotulo: 'RiskMind mais brando', tom: 'atencao', detalhe: atual,
      texto: comum + ' Aqui o motor e MAIS brando que a agencia.',
    };
  }
  return {
    chave: 'convergencia', rotulo: 'convergencia', tom: 'bom', detalhe: atual,
    texto: comum + ' As duas leituras ocupam posicao equivalente em suas escalas.',
  };
}
